import React from 'react';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { UpdateOrderRequest } from '@atlassian/cssxp-typescommon';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { Text } from '@atlaskit/primitives';
import { N0 } from '@atlaskit/theme/colors';
import {
  CustomPanel,
  Divider,
  EntitlementIcon,
  ErrorComponents,
  errorNotificationBuilder,
  InfoPanel,
  NotificationTypes,
  successNotificationBuilder,
  SummaryPanelContainer,
  SummaryPanelRow,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { isCollectionProductEntitlement, pluralize } from '@css-xp/ppc-utils';
import { withApollo } from '@css-xp/base/graphql-provider';
import { analytics, AnalyticsTrackSubject, AnalyticsTrackAction } from '@atlassian/temp-analytics';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { HelpContent, NoData } from '@css-xp/ppc-common';
import { useConsentHub } from '@css-xp/ppc-consent-hub';
import { EditSubscriptionTermsAndConditions } from '../../components/terms-and-conditions/terms-and-conditions-details';
import { getLinkedEntitlementsForCollectionOptOutTrial } from './api';
import { OptoutTrialContainer } from './optout-trial-pane.styles';
import './optout-trial-pane.scss';

interface Props {
  entitlementId: string;
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  isDesignIteration1Enabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}

type OfferingName = string | undefined | null;

export const getWarningBannerContent = (OfferingName: OfferingName = '') => {
  const offeringPlanName = OfferingName && OfferingName + ' ';
  const content = `By continuing, the subscription will lose access to all ${offeringPlanName}features, and have less
  storage. The ${offeringPlanName}trial will end immediately.`;
  return content.replace(/\s\s+/g, ' ');
};

export const getConsentContent = (OfferingName: OfferingName = '') => {
  const offeringPlanName = OfferingName && OfferingName + ' ';
  const content = `By selecting ‘End trial’, you agree that Atlassian will continue to charge you based on the
  existing ${offeringPlanName}plan. The cost will reflect in your next billing cycle.`;
  return content.replace(/\s\s+/g, ' ');
};

const getNotificationContent = (upgradeTrial: boolean) => {
  return (
    <div>
      {upgradeTrial ? (
        <p>
          The subscription will remain on the existing plan. You can view the changes in <strong>Order history</strong>
        </p>
      ) : (
        <p>
          The subscription has been changed. A new invoice will be available in 24 hours. You can view the changes in{' '}
          <strong>Order history</strong>
        </p>
      )}
      <Link to={`#order-history`}>View order history</Link>
    </div>
  );
};

export const OptoutTrial = ({
  entitlementId,
  entitlement,
  refetch,
  isDesignIteration1Enabled,
  isTWCPhase2Enabled,
  isAppEditionSupportEnabled,
}: Props) => {
  const { closeSidebar } = useSidebar();
  const productName = entitlement.productName;
  const isOnUpgradeTrial = !!entitlement.upgradeTrial;
  const isApp = entitlement?.isApp;

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const currentOffering = entitlement?.offering?.name;

  const currentTrialOffering = isOnUpgradeTrial ? entitlement?.upgradeTrial?.offeringName : entitlement?.offering?.name;

  const consentText = getConsentContent(currentOffering);

  const { updateCustomerConsent } = useConsentHub();

  const { sendSuccessNotification, sendErrorNotification, notificationSent, setNotificationSent } = useNotification();

  const getNotificationOption = (id: string, header: string, upgradeTrial: EntitlementDetails['upgradeTrial']) => {
    const notificationOptions = successNotificationBuilder();

    return notificationOptions
      ?.withId(id)
      .withHeader(header)
      .withMessage(getNotificationContent(!!upgradeTrial))
      .withDismissAfter(3000)
      .isVersion(NotificationTypes.FLAG)
      .build();
  };
  if (complete && !notificationSent) {
    const notificationOptions = getNotificationOption('optout-trial-success', 'Trial ended', entitlement.upgradeTrial);
    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.CHANGED,
      actionSubject: AnalyticsTrackSubject.OPTOUT_TRIAL,
      attributes: {
        transactionAccountId: entitlement?.transactionAccountId,
        entitlementId: entitlement?.id,
      },
    });
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  if (formError) {
    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.FAILED,
      actionSubject: AnalyticsTrackSubject.OPTOUT_TRIAL,
      attributes: {
        transactionAccountId: entitlement?.transactionAccountId,
        entitlementId: entitlement?.id,
      },
    });
  }

  const constructRequestData = async (entitlementId: string) => {
    const requestData: UpdateOrderRequest = {
      entitlementId: entitlementId,
      isOptOutTrial: true,
      productId: entitlement?.productId ?? '',
      units: entitlement.activeUsers?.toString() ?? '',
    };

    if (isTWCPhase2Enabled && isCollectionProductEntitlement(entitlement?.productId)) {
      const response = await getLinkedEntitlementsForCollectionOptOutTrial(
        entitlementId,
        entitlement?.transactionAccountId ?? ''
      );
      const linkedEntitlements = response?.data?.entitlementDetails?.linkedEntitlements ?? [];

      requestData.linkedEntitlements = [
        ...linkedEntitlements?.map((entitlement) => ({
          entitlementId: entitlement?.entitlementId ?? '',
          productId: entitlement?.productId ?? '',
        })),
        { entitlementId: entitlementId, productId: entitlement?.productId ?? '' },
      ];
    }

    return requestData;
  };

  const handleFormSubmit = async () => {
    try {
      const requestData = await constructRequestData(entitlementId);
      await updateCustomerConsent(consentText);
      submitForm(requestData);
    } catch {
      const errorNotificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('opt-out-trial-error')
        .withHeader("Sorry, we couldn't opt you out of the trial")
        .withMessage('Some error has occurred')
        .withDismissAfter(5000)
        .build();
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.FAILED,
        actionSubject: AnalyticsTrackSubject.OPTOUT_TRIAL,
        attributes: {
          transactionAccountId: entitlement?.transactionAccountId,
          entitlementId: entitlement?.id,
        },
      });
      sendErrorNotification(errorNotificationOptions);
      setNotificationSent(true);
    }
  };

  const renderCurrentPlanDetails = () => {
    if (isDesignIteration1Enabled) {
      const getPricingPlan = () => {
        if (entitlement?.pricingPlan) {
          const { pricingPlanType, status, description } = entitlement?.pricingPlan || {};
          return (
            <>
              {description} | {sentenceCase(pricingPlanType)} | {sentenceCase(status)}
            </>
          );
        }

        return <NoData />;
      };

      const getActiveUsers = () => {
        if (entitlement.activeUsers) {
          return pluralize(entitlement.activeUsers, 'user');
        }
        return <NoData />;
      };

      const getBillingCycle = () => {
        return entitlement.billingFrequency ? sentenceCase(entitlement.billingFrequency) : <NoData />;
      };

      return (
        <>
          <Text weight="semibold">Existing {currentTrialOffering} plan</Text>
          <SummaryPanelRow
            label="Product name"
            value={
              <div className="flex align-center gap1">
                <EntitlementIcon
                  logoId={entitlement?.logoId}
                  productId={entitlement?.productId}
                  className="avatar medium"
                  isCloud={true}
                />
                <div className="flex-column">
                  <p className="text-ellipsis">{entitlement.productName}</p>
                  <Text color="color.text.subtlest" size="small">
                    {entitlement.entitlementNumber}
                  </Text>
                </div>
              </div>
            }
          />
          <CustomPanel
            testId="more-plan-details"
            expandedHeader="More plan details"
            collapsedHeader="More plan details"
            version={3}
            padding="20px"
            backgroundColor={N0}
          >
            <section className="more-plan-details">
              <SummaryPanelRow label="Billing cycle" value={getBillingCycle()} />
              <SummaryPanelRow label="Pricing plan" value={getPricingPlan()} />
              <SummaryPanelRow label="Quantity" value={getActiveUsers()} />
            </section>
          </CustomPanel>
        </>
      );
    }

    return (
      <>
        <SummaryPanelRow
          label="Current plan"
          value={currentOffering ?? <NoData testId="current-offering-plan-no-data" />}
          testId="current-offering-plan"
        />
        <SummaryPanelRow
          label="Trial plan"
          value={currentTrialOffering ?? <NoData testId="trial-offering-plan-no-data" />}
          testId="trial-offering-plan"
        />
      </>
    );
  };

  const renderFooter = () => {
    const closeButtonText = isDesignIteration1Enabled ? 'Cancel' : 'Back';
    const confirmButtonText = isDesignIteration1Enabled ? 'Confirm' : 'End trial';
    return (
      <ButtonGroup>
        <Button appearance="subtle" onClick={() => closeSidebar()} testId="cancel" className="close-sidebar-btn">
          {closeButtonText}
        </Button>
        <LoadingButton
          appearance={isDesignIteration1Enabled ? 'warning' : 'primary'}
          type="submit"
          isLoading={isProcessing}
          testId={'optout-trial'}
          aria-label="Optout trial"
        >
          {confirmButtonText}
        </LoadingButton>
      </ButtonGroup>
    );
  };

  const renderFormBody = () => {
    if (isDesignIteration1Enabled) {
      return null;
    }
    return (
      <>
        <div className="current-info-section" data-testid="current-info">
          <h1 className="product-header">You're currently trialing</h1>
          <h3 className="product-name" data-testid="product-name">{`${productName}`}</h3>
        </div>
        <div className="warning-container" data-testid="warning-container">
          <SectionMessage appearance="warning">
            <h3 className="warning-header">Feature access</h3>
            <p>{getWarningBannerContent(currentTrialOffering)}</p>
          </SectionMessage>
        </div>
        <FormSection>
          <Divider />
          <EditSubscriptionTermsAndConditions
            isConfirmationCheckboxRequired={false}
            tncTexts={[
              (props) => (
                <p key="tnc-text-1" {...props}>
                  {consentText}
                </p>
              ),
            ]}
          />
        </FormSection>
      </>
    );
  };

  const renderFormFooter = () => {
    if (isDesignIteration1Enabled) {
      return (
        <FormFooter>
          <Text color="color.text.subtle">
            By confirming, you agree that Atlassian will continue to charge you based on the existing plan. The cost
            will reflect in your next billing cycle.
          </Text>
          {renderFooter()}
        </FormFooter>
      );
    }
    return (
      <FormFooter align="end">
        <span className="form-footer-btn-wrapper">{renderFooter()}</span>
      </FormFooter>
    );
  };

  const renderWarningBanner = () => {
    if (isDesignIteration1Enabled) {
      return isAppEditionSupportEnabled && isApp ? (
        <SectionMessage appearance="warning">
          <p>Your customer will lose all advanced features.</p>
        </SectionMessage>
      ) : (
        <div className="warning-container" data-testid="warning-container">
          <SectionMessage appearance="warning">
            <h3 className="warning-header">Feature access</h3>
            <p>Premium features, including more storage, will be lost.</p>
          </SectionMessage>
        </div>
      );
    }
  };

  return (
    <OptoutTrialContainer
      isDesignIteration1Enabled={isDesignIteration1Enabled}
      className={classNames('optout-trial-container', { 'optout-trial-container-new ': isDesignIteration1Enabled })}
      data-testid="optout-trial-container"
    >
      <div className="optout-trial-info">
        <div className="header">
          <FormHeader title={`End ${currentTrialOffering} trial`} />
        </div>
        {renderWarningBanner()}
        <div className="product-detail-info" data-testid="product-detail-info">
          <SummaryPanelContainer isHeaderContentHidden={true}>{renderCurrentPlanDetails()}</SummaryPanelContainer>
        </div>
        <Form onSubmit={handleFormSubmit}>
          {({ formProps }) => (
            <form {...formProps}>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to opt out for the following reason"
                      message={formError}
                      helpContent={<HelpContent />}
                    />
                  }
                />
              )}
              {renderFormBody()}
              {renderFormFooter()}
            </form>
          )}
        </Form>
      </div>
    </OptoutTrialContainer>
  );
};

export default withApollo(OptoutTrial);
